import React, { useMemo, useState } from 'react';
import Dialog from '../../../organisms/Dialog';
import SearchFilter from '../../../atoms/SearchFilter';
import { MessageComposition } from '../../../../types/communication';
import { ChannelTile } from '../composition/shared/channelVisuals';

interface UseInCompositionDialogProps {
    show: boolean;
    compositions: MessageComposition[];
    onClose: () => void;
    onPick: (composition: MessageComposition) => void;
}

const UseInCompositionDialog: React.FC<UseInCompositionDialogProps> = ({ show, compositions, onClose, onPick }) => {
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<MessageComposition | null>(null);

    const filtered = useMemo(() => {
        const term = search.trim().toLocaleLowerCase('pt-BR');
        return compositions.filter((c) => !term || `${c.name} ${c.description}`.toLocaleLowerCase('pt-BR').includes(term));
    }, [compositions, search]);

    const close = () => {
        setSearch('');
        setSelected(null);
        onClose();
    };

    return (
        <Dialog
            id="use-in-composition"
            show={show}
            onClose={close}
            size="md"
            header="Usar em uma composição"
            body={
                <div className="flex flex-col gap-3">
                    <p className="text-xs text-gray-500">
                        Aparecem as composições do mesmo canal que aceitam este modelo. Depois de vinculado, o modelo só se edita
                        dentro da composição.
                    </p>
                    <SearchFilter value={search} onChange={setSearch} placeholder="Buscar composição..." />
                    <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-slate-700 rounded-lg border border-gray-200 dark:border-slate-700">
                        {filtered.map((c) => (
                            <li key={c.id}>
                                <button
                                    type="button"
                                    onClick={() => setSelected(c)}
                                    className={`w-full flex items-center gap-3 px-3 py-2 text-left ${
                                        selected?.id === c.id ? 'bg-teal-50 dark:bg-teal-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                    }`}
                                >
                                    <ChannelTile channel={c.channel} size="sm" />
                                    <span className="min-w-0">
                                        <span className="block text-sm text-gray-800 dark:text-gray-100 truncate">{c.description || c.name}</span>
                                        <span className="block text-[11px] font-mono text-gray-400 truncate">{c.name}</span>
                                    </span>
                                </button>
                            </li>
                        ))}
                        {!filtered.length && <li className="px-3 py-4 text-sm text-gray-400">Nenhuma composição compatível.</li>}
                    </ul>
                </div>
            }
            footer={
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={close} className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-100">
                        Cancelar
                    </button>
                    <button
                        type="button"
                        disabled={!selected}
                        onClick={() => {
                            if (selected) onPick(selected);
                            close();
                        }}
                        className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-40"
                    >
                        Vincular
                    </button>
                </div>
            }
        />
    );
};

export default UseInCompositionDialog;
